<template>
  <div v-if="isAllowed" class="activity-agenda-link">
    <woot-button
      class-names="activity-agenda-link--button"
      variant="clear"
      color-scheme="secondary"
      :class="{ 'is-active': isAgendaActive }"
      :title="$t('ACTIVITY_AGENDA.COUNTER')"
      :aria-label="$t('ACTIVITY_AGENDA.COUNTER')"
      @click="openAgenda"
    >
      <fluent-icon icon="clipboard-task" />
      <span v-if="badgeCount" class="badge warning">{{ badgeCount }}</span>
    </woot-button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { debounce } from '@chatwoot/utils';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  ACTIVITY_AGENDA_REFRESH_DELAY,
  ACTIVITY_AGENDA_ROUTE_NAME,
  formatAgendaBadgeCount,
  reconnectRefreshDelay,
} from 'dashboard/helper/activityAgendaHelper';

const DAY_CHECK_INTERVAL = 60000;

const REFRESH_EVENTS = [
  'opportunity_activities_changed',
  'opportunity_updated',
  'opportunity_created',
];

export default {
  name: 'ActivityAgendaCounter',
  data() {
    return {
      currentDay: new Date().toDateString(),
      dayTimer: null,
      reconnectTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      summary: 'activityAgenda/getSummary',
    }),
    isAllowed() {
      return (
        !!this.accountId &&
        this.$store.getters['accounts/isOpportunityKanbanAccessAllowed'](
          this.accountId
        )
      );
    },
    badgeCount() {
      return formatAgendaBadgeCount(this.summary.todayCount);
    },
    isAgendaActive() {
      return this.$route.name === ACTIVITY_AGENDA_ROUTE_NAME;
    },
  },
  watch: {
    isAllowed: {
      immediate: true,
      handler(allowed) {
        if (allowed) this.refresh();
      },
    },
    accountId(newId, oldId) {
      if (newId !== oldId && this.isAllowed) this.refresh();
    },
  },
  created() {
    this.scheduleRefresh = debounce(
      this.refresh,
      ACTIVITY_AGENDA_REFRESH_DELAY
    );
  },
  mounted() {
    REFRESH_EVENTS.forEach(event => window.bus.$on(event, this.onChange));
    window.bus.$on(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.dayTimer = window.setInterval(this.checkDayChange, DAY_CHECK_INTERVAL);
  },
  beforeDestroy() {
    REFRESH_EVENTS.forEach(event => window.bus.$off(event, this.onChange));
    window.bus.$off(BUS_EVENTS.WEBSOCKET_RECONNECT, this.onReconnect);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.clearInterval(this.dayTimer);
    window.clearTimeout(this.reconnectTimer);
  },
  methods: {
    refresh() {
      if (!this.isAllowed) return Promise.resolve();
      return this.$store
        .dispatch('activityAgenda/fetchSummary')
        .catch(() => null);
    },
    onChange() {
      if (this.isAllowed) this.scheduleRefresh();
    },
    onReconnect() {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = window.setTimeout(
        this.onChange,
        reconnectRefreshDelay()
      );
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.onChange();
    },
    checkDayChange() {
      const today = new Date().toDateString();
      if (today === this.currentDay) return;
      this.currentDay = today;
      this.refresh();
    },
    openAgenda() {
      if (this.isAgendaActive) return;
      this.$router.push(
        frontendURL(`accounts/${this.accountId}/opportunities-agenda`)
      );
    },
  },
};
</script>

<style scoped lang="scss">
.badge {
  position: absolute;
  right: 0;
  top: var(--space-smaller);
}

.activity-agenda-link {
  margin: auto 0;

  @media screen and (max-width: 768px) {
    display: none;
  }

  .activity-agenda-link--button {
    display: flex;
    position: relative;
    border-radius: var(--border-radius-large);
    border: 1px solid transparent;
    color: var(--s-600);
    margin-right: var(--space-smaller);

    &:hover {
      background: var(--w-50);
      color: var(--s-600);
    }

    &:focus {
      border-color: var(--c-500);
    }

    &.is-active {
      background: var(--w-50);
      color: var(--c-500);
    }
  }
}
</style>
